import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useUpdateProduct } from "./use-update-product";
import { PRODUCT_DESCRIPTION_MAX_LENGTH, PRODUCT_NAME_MAX_LENGTH } from "./constants";
import { ProductMetadataFields } from "./ProductMetadataFields";
import {
  buildUpdateMetadataPatch,
  isRankInputValid,
  parseProductMetadata,
  productMetadataEquals,
  productMetadataToValue,
  valueToProductMetadata,
  type ProductMetadataValue,
} from "./product-metadata";
import type { StripeProduct, UpdateProductInput } from "./types";

interface UpdateProductDialogProps {
  readonly product: StripeProduct | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function UpdateProductDialog({
  product,
  open,
  onOpenChange,
  onSuccess,
}: UpdateProductDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [metadata, setMetadata] = useState<ProductMetadataValue>({
    tier: "",
    access: [],
    rank: "",
  });
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateProduct();

  useEffect(() => {
    if (open && product) {
      setName(product.name);
      setDescription(product.description ?? "");
      setActive(product.active);
      setMetadata(productMetadataToValue(parseProductMetadata(product.metadata)));
      setError(null);
    }
  }, [open, product]);

  if (!product) return null;

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isValid =
    trimmedName.length > 0 &&
    trimmedName.length <= PRODUCT_NAME_MAX_LENGTH &&
    trimmedDescription.length <= PRODUCT_DESCRIPTION_MAX_LENGTH &&
    isRankInputValid(metadata.rank);

  const originalMetadata = parseProductMetadata(product.metadata);
  const metadataChanged = !productMetadataEquals(
    originalMetadata,
    valueToProductMetadata(metadata),
  );

  const isDirty =
    trimmedName !== product.name ||
    trimmedDescription !== (product.description ?? "") ||
    active !== product.active ||
    metadataChanged;

  const handleClose = (next: boolean) => {
    if (!next && update.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!isValid || !isDirty) return;
    setError(null);
    const input: UpdateProductInput = {
      ...(trimmedName !== product.name ? { name: trimmedName } : {}),
      ...(trimmedDescription !== (product.description ?? "")
        ? { description: trimmedDescription }
        : {}),
      ...(active !== product.active ? { active } : {}),
      ...(metadataChanged ? { metadata: buildUpdateMetadataPatch(metadata) } : {}),
    };
    update.mutate(
      { id: product.id, input },
      {
        onSuccess: () => onSuccess(),
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "catalog.errors.forbidden",
              serverPrefixKey: "catalog.errors.serverPrefix",
              genericKey: "catalog.products.update.error",
            }),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="update-product-dialog">
        <DialogHeader>
          <DialogTitle>{t("catalog.products.update.title", { name: product.name })}</DialogTitle>
          <DialogDescription>{t("catalog.products.update.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <label
              htmlFor="update-product-name"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.products.fields.name")}
            </label>
            <Input
              id="update-product-name"
              data-testid="update-product-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={PRODUCT_NAME_MAX_LENGTH}
              disabled={update.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="update-product-description"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("catalog.products.fields.description")}
            </label>
            <Textarea
              id="update-product-description"
              data-testid="update-product-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
              rows={3}
              disabled={update.isPending}
            />
          </div>

          <label className="flex items-center gap-2 font-sans text-sm">
            <input
              type="checkbox"
              data-testid="update-product-active-input"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={update.isPending}
            />
            <span>{t("catalog.products.fields.active")}</span>
          </label>

          <ProductMetadataFields
            value={metadata}
            onChange={setMetadata}
            disabled={update.isPending}
            idPrefix="update-product"
          />

          {error && (
            <p
              data-testid="update-product-error"
              role="alert"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={update.isPending}>
            {t("catalog.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-update-product-button"
            onClick={handleSubmit}
            disabled={!isValid || !isDirty || update.isPending}
          >
            {update.isPending ? t("catalog.products.update.pending") : t("catalog.actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
